import { useCallback, useRef } from 'react';
import { useSession } from 'next-auth/react';

import type { CommentListSort } from '@/features/comments/types/publication-kind';
import { Comment, CreateCommentData } from '@/types/comment';
import { buildDirectBackendUrl, createDirectBackendHeaders } from '@/lib/direct-backend-api';
import { parseApiErrorMessage } from '@/features/comments/utils/parse-api-error-message.util';
import { EMPTY_COMMENTS, useCommentsStore } from '../store/comments.store';

interface UseCommentsReturn {
  comments: Comment[];
  loading: boolean;
  error: string | null;
  fetchComments: (
    articleId?: number,
    postId?: number,
    chapterId?: string,
    paragraphId?: string,
    page?: number,
    sort?: CommentListSort,
  ) => Promise<{ hasNextPage: boolean; totalDocs: number } | undefined>;
  fetchReplies: (parentCommentId: number) => Promise<Comment[]>;
  createComment: (data: CreateCommentData) => Promise<Comment | null>;
  updateComment: (id: number, content: string) => Promise<{ ok: boolean; message?: string }>;
  deleteComment: (id: number) => Promise<{ ok: boolean; message?: string }>;
  reportComment: (
    commentId: number,
    category: string,
    reason: string,
    details?: string,
  ) => Promise<{ ok: boolean; message?: string }>;
}

export function useComments(scopeKey: string): UseCommentsReturn {
  const { data: session } = useSession();
  const accessToken = session?.accessToken;
  const userId = session?.user?.id;
  const comments = useCommentsStore((state) => state.scopes[scopeKey]?.comments ?? EMPTY_COMMENTS);
  const loading = useCommentsStore((state) => state.scopes[scopeKey]?.loading ?? false);
  const error = useCommentsStore((state) => state.scopes[scopeKey]?.error ?? null);
  const setComments = useCommentsStore((state) => state.setComments);
  const appendComments = useCommentsStore((state) => state.appendComments);
  const addComment = useCommentsStore((state) => state.addComment);
  const addReply = useCommentsStore((state) => state.addReply);
  const updateCommentInStore = useCommentsStore((state) => state.updateComment);
  const deleteCommentFromStore = useCommentsStore((state) => state.deleteComment);
  const setLoading = useCommentsStore((state) => state.setLoading);
  const setError = useCommentsStore((state) => state.setError);
  const latestListRequestRef = useRef(0);

  const fetchComments = useCallback(
    async (
      articleId?: number,
      postId?: number,
      chapterId?: string,
      paragraphId?: string,
      page: number = 1,
      sort?: CommentListSort,
    ): Promise<{ hasNextPage: boolean; totalDocs: number } | undefined> => {
      const requestId = page === 1 ? ++latestListRequestRef.current : latestListRequestRef.current;

      try {
        setLoading(scopeKey, true);
        setError(scopeKey, null);

        const params = new URLSearchParams();
        if (articleId) params.append('articleId', articleId.toString());
        if (postId) params.append('postId', postId.toString());
        if (chapterId) params.append('chapterId', chapterId);
        if (paragraphId) params.append('paragraphId', paragraphId);
        if (sort && sort !== 'createdAt:desc') params.append('sort', sort);
        params.append('page', page.toString());
        params.append('limit', '20');

        const response = await fetch(buildDirectBackendUrl(`/api/comments?${params.toString()}`), {
          headers: createDirectBackendHeaders({
            accessToken,
          }),
        });

        if (requestId !== latestListRequestRef.current) return undefined;

        if (!response.ok) {
          throw new Error('Failed to fetch comments');
        }

        const data: { docs?: Comment[]; hasNextPage?: boolean; totalDocs?: number } | Comment[] =
          await response.json();
        if (requestId !== latestListRequestRef.current) return undefined;

        const result = Array.isArray(data)
          ? { docs: data, hasNextPage: false, totalDocs: data.length }
          : data;
        const docs = result.docs ?? [];
        const filteredComments = docs.filter((item: Comment) => item.content !== '__READ_MARKER__');

        const hasNextPage =
          Boolean(result.hasNextPage) || (!Array.isArray(data) && filteredComments.length >= 20);

        if (page === 1) {
          setComments(scopeKey, filteredComments);
        } else {
          appendComments(scopeKey, filteredComments);
        }

        return {
          hasNextPage,
          totalDocs: result.totalDocs ?? 0,
        };
      } catch (err) {
        if (requestId !== latestListRequestRef.current) return undefined;
        setError(scopeKey, err instanceof Error ? err.message : 'Unknown error');
        return undefined;
      } finally {
        if (requestId === latestListRequestRef.current) {
          setLoading(scopeKey, false);
        }
      }
    },
    [accessToken, appendComments, scopeKey, setComments, setError, setLoading],
  );

  const fetchReplies = useCallback(
    async (parentCommentId: number): Promise<Comment[]> => {
      try {
        const params = new URLSearchParams();
        params.append('parentCommentId', parentCommentId.toString());

        const response = await fetch(buildDirectBackendUrl(`/api/comments?${params.toString()}`), {
          headers: createDirectBackendHeaders({
            accessToken,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch replies');
        }

        const data: { docs?: Comment[] } | Comment[] = await response.json();
        return Array.isArray(data) ? data : (data.docs ?? []);
      } catch (err) {
        console.error('Error fetching replies:', err);
        return [];
      }
    },
    [accessToken],
  );

  const createComment = useCallback(
    async (data: CreateCommentData): Promise<Comment | null> => {
      try {
        setError(scopeKey, null);
        console.log('Creating comment with data:', data);

        if (!accessToken || !userId) {
          throw new Error('Требуется авторизация');
        }
        // authorId не отправляем — автор берётся из JWT на бэке
        const response = await fetch(buildDirectBackendUrl('/api/comments'), {
          method: 'POST',
          headers: createDirectBackendHeaders({
            accessToken,
            hasJsonContentType: true,
          }),
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error('Failed to create comment');
        }

        const newComment = await response.json();
        console.log('Comment created successfully:', newComment);

        if (data.parentComment) {
          addReply(scopeKey, data.parentComment);
        } else {
          addComment(scopeKey, newComment);
        }

        return newComment;
      } catch (err) {
        console.error('Error in createComment:', err);
        setError(scopeKey, err instanceof Error ? err.message : 'Unknown error');
        return null;
      }
    },
    [addComment, addReply, accessToken, scopeKey, setError, userId],
  );

  const updateComment = useCallback(
    async (id: number, content: string): Promise<{ ok: boolean; message?: string }> => {
      try {
        setError(scopeKey, null);

        if (!accessToken || !userId) {
          return { ok: false, message: 'Требуется авторизация' };
        }
        const response = await fetch(buildDirectBackendUrl(`/api/comments/${id}`), {
          method: 'PATCH',
          headers: createDirectBackendHeaders({
            accessToken,
            hasJsonContentType: true,
          }),
          body: JSON.stringify({ content }),
        });

        if (!response.ok) {
          const message = await parseApiErrorMessage(response, 'Не удалось сохранить комментарий');
          setError(scopeKey, message);
          return { ok: false, message };
        }

        updateCommentInStore(scopeKey, id, content);
        return { ok: true };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Не удалось сохранить комментарий';
        setError(scopeKey, message);
        return { ok: false, message };
      }
    },
    [accessToken, scopeKey, setError, updateCommentInStore, userId],
  );

  const deleteComment = useCallback(
    async (id: number): Promise<{ ok: boolean; message?: string }> => {
      try {
        setError(scopeKey, null);

        if (!accessToken || !userId) {
          return { ok: false, message: 'Требуется авторизация' };
        }
        const response = await fetch(buildDirectBackendUrl(`/api/comments/${id}`), {
          method: 'DELETE',
          headers: createDirectBackendHeaders({
            accessToken,
          }),
        });

        if (!response.ok) {
          const message = await parseApiErrorMessage(response, 'Не удалось удалить комментарий');
          setError(scopeKey, message);
          return { ok: false, message };
        }

        deleteCommentFromStore(scopeKey, id);
        return { ok: true };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Не удалось удалить комментарий';
        setError(scopeKey, message);
        return { ok: false, message };
      }
    },
    [accessToken, deleteCommentFromStore, scopeKey, setError, userId],
  );

  const reportComment = useCallback(
    async (
      commentId: number,
      category: string,
      reason: string,
      details?: string,
    ): Promise<{ ok: boolean; message?: string }> => {
      try {
        setError(scopeKey, null);

        if (!accessToken || !userId) {
          return { ok: false, message: 'Требуется авторизация' };
        }
        const response = await fetch(buildDirectBackendUrl(`/api/comments/${commentId}/report`), {
          method: 'POST',
          headers: createDirectBackendHeaders({
            accessToken,
            hasJsonContentType: true,
          }),
          body: JSON.stringify({ category, reason, details: details || undefined }),
        });

        if (!response.ok) {
          const message = await parseApiErrorMessage(response, 'Не удалось отправить жалобу');
          setError(scopeKey, message);
          return { ok: false, message };
        }

        return { ok: true };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Не удалось отправить жалобу';
        setError(scopeKey, message);
        return { ok: false, message };
      }
    },
    [accessToken, scopeKey, setError, userId],
  );

  return {
    comments,
    loading,
    error,
    fetchComments,
    fetchReplies,
    createComment,
    updateComment,
    deleteComment,
    reportComment,
  };
}
